import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import {
  ClientProfile_MissingDocumentFragment,
  HouseholdMissingDocumentFragment,
  useGetHouseholdMissingDocumentsQuery,
} from '../graphql/MissingDocuments.generated';
import {
  filterEligibleClientProfiles,
  filterNonOverriddenMissingDocuments,
  getHouseholdMissingDocuments,
  getOtherClientMissingDocuments,
  getPrimaryClientMissingDocuments,
} from '../missingDocuments.utils';

interface MissingDocumentsContextValue {
  isShowing: boolean;
  show: () => void;
  hide: () => void;
  loading: boolean;
  hasOverrides: boolean;
  primaryClientProfileMissingDocuments: Record<
    string,
    HouseholdMissingDocumentFragment[]
  >;
  otherClientProfileMissingDocuments: Record<
    string,
    HouseholdMissingDocumentFragment[]
  >;
  householdMissingDocuments: HouseholdMissingDocumentFragment[];
  clientProfiles: ClientProfile_MissingDocumentFragment[];
  missingDocuments: HouseholdMissingDocumentFragment[];
  count: number;
}

const MissingDocumentsContext =
  createContext<MissingDocumentsContextValue | null>(null);

interface MissingDocumentsContextProviderProps {
  children: ReactNode;
  pollForUpdates?: boolean;
}

export function MissingDocumentsContextProvider({
  children,
  pollForUpdates = false,
}: MissingDocumentsContextProviderProps) {
  const [isShowing, setIsShowing] = useState(false);

  const show = useCallback(() => {
    setIsShowing(true);
  }, []);

  const hide = useCallback(() => {
    setIsShowing(false);
  }, []);

  const { householdId } = useHouseholdDetailsContext();
  const { data, loading } = useGetHouseholdMissingDocumentsQuery({
    pollInterval: pollForUpdates ? 3000 : 0,
    variables: {
      householdID: householdId!,
    },
    skip: !householdId,
  });

  const clientProfiles = useMemo(
    () => filterEligibleClientProfiles(data?.clientProfiles),
    [data?.clientProfiles]
  );

  const hasOverrides = (data?.householdMissingDocuments ?? []).some(
    (d) => d.isOverridden
  );

  // Extract data from props (fallback for documents)
  const missingDocuments: HouseholdMissingDocumentFragment[] = useMemo(() => {
    // Otherwise use the new query data
    return filterNonOverriddenMissingDocuments(data?.householdMissingDocuments);
  }, [data?.householdMissingDocuments]);

  // Extract household missing documents (just the document types)
  const householdMissingDocuments = useMemo(
    () => getHouseholdMissingDocuments(missingDocuments),
    [missingDocuments]
  );

  // Group missing documents by primary client profile IDs
  const primaryClientProfileMissingDocuments = useMemo(
    () => getPrimaryClientMissingDocuments(missingDocuments),
    [missingDocuments]
  );

  const otherClientProfileMissingDocuments = useMemo(
    () => getOtherClientMissingDocuments(missingDocuments),
    [missingDocuments]
  );

  const count = missingDocuments.length;

  const value = useMemo(() => {
    return {
      isShowing,
      show,
      hide,
      householdMissingDocuments,
      primaryClientProfileMissingDocuments,
      otherClientProfileMissingDocuments,
      count,
      loading,
      missingDocuments,
      hasOverrides,
      clientProfiles,
    };
  }, [
    isShowing,
    show,
    hide,
    householdMissingDocuments,
    primaryClientProfileMissingDocuments,
    otherClientProfileMissingDocuments,
    count,
    loading,
    missingDocuments,
    hasOverrides,
    clientProfiles,
  ]);

  return (
    <MissingDocumentsContext.Provider value={value}>
      {children}
    </MissingDocumentsContext.Provider>
  );
}

export function useMissingDocumentsContext() {
  return useGuardedContext(
    MissingDocumentsContext,
    'MissingDocumentsContextProvider'
  );
}
